import { useCallback } from 'react';

import useUserData from 'approot/shared/user/user-data';
import {
  openAdminModal,
  openInactiveAccountModal,
} from 'approot/global-notifications/global-notification.utils';
import { chooseUnauthorisedPaywall } from 'approot/shared/logic/choose-unauthorised-paywall';
import { setGlobalModal } from 'approot/global-notifications/global-modals.apollo';
import { ModalKey } from 'approot/shared/modal/modal.constants';

export const useAuthorisedAction = () => {
  const {
    isAlreadySignedInOnce,
    isAccountExpired,
    isSchoolTeacher,
    isSchoolAdmin,
    isSignedIn,
  } = useUserData();

  const unauthorisedAccessModal = chooseUnauthorisedPaywall({
    isAlreadySignedInOnce,
    isSignedIn,
    isAccountExpired,
    isSchoolAdmin,
    isSchoolTeacher,
  });

  const unauthorisedAccessHref = useCallback(() => {
    const modalType = unauthorisedAccessModal;
    if (modalType === ModalKey.NONE) {
      return '';
    } else {
      return `?modal=${modalType}`;
    }
  }, [unauthorisedAccessModal]);

  const authoriseActiveTeacher = useCallback(
    (callback: Function) => {
      if (isAccountExpired) {
        openInactiveAccountModal();
      } else if (!isSchoolTeacher && isSchoolAdmin) {
        openAdminModal();
      } else {
        callback();
      }
    },
    [isAccountExpired, isSchoolAdmin, isSchoolTeacher]
  );

  const authoriseActiveUser = useCallback(
    (callback: Function) => {
      if (isAccountExpired) {
        openInactiveAccountModal();
      } else {
        callback();
      }
    },
    [isAccountExpired]
  );

  const authoriseShowPaywall = useCallback(() => {
    setGlobalModal(unauthorisedAccessModal);
  }, [unauthorisedAccessModal]);

  const authoriseAction = <F extends Function>(
    isAuthorised: boolean,
    handleAuthSuccess: F,
    handleAuthFailure: F
  ): F => (isAuthorised ? handleAuthSuccess : handleAuthFailure);

  return {
    authoriseActiveTeacher,
    authoriseActiveUser,
    authoriseShowPaywall,
    authoriseAction,
    unauthorisedAccessModal,
    unauthorisedAccessHref,
  };
};
